/**
 * Le compte, l'espace, l'élève affiché, ses élèves — les seules lectures de `currentUser` des ports.
 * Un lecteur dit s'il parle du compte ou de l'élève qu'il montre : les deux se confondent chez
 * l'élève et le parent (l'enfant affiché EST le compte), et se séparent chez le tuteur, qui lit
 * l'élève qu'il regarde par la même voie. La règle : `ports/CLAUDE.md` § « Le compte et l'élève
 * affiché » ; ESLint refuse toute autre lecture de `currentUser` dans les ports.
 *
 * Chaque lecture rend l'objet du store tel quel, jamais une copie : une entrée de `createSelector`
 * garde sa mémoïsation.
 */
import { createSelector } from "reselect"

import { hasRole } from "../../../../data/utility/ability"

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ReduxState = any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type StoredUser = any

type StudentId = string | number | null | undefined

/** L'espace de qui regarde : `parent` en mode parent, `tutor`, `student`, `guest` sans compte. */
export type Space = "parent" | "tutor" | "student" | "guest"

const NO_STUDENTS: StoredUser[] = []

/** Le compte connecté — ses rôles, son impersonation, son fuseau, sa connexion. Jamais « l'élève ». */
export function selectAccount(state: ReduxState): StoredUser | null {
    return state?.data?.currentUser?.object ?? null
}

export function selectSpace(state: ReduxState): Space {
    const account = selectAccount(state)
    if (account == null) return "guest"
    if (state?.data?.family?.isParentMode) return "parent"
    // Le candidat passe sa candidature dans l'espace tuteur, s'il n'est pas aussi élève.
    if (hasRole(account, "tutor")) return "tutor"
    return hasRole(account, "tutor-candidate") && !hasRole(account, "student") ? "tutor" : "student"
}

/** Le compte est-il l'élève qu'on regarde ? Chez l'élève et le parent, oui ; chez le tuteur, jamais. */
function accountIsTheStudent(state: ReduxState): boolean {
    const space = selectSpace(state)
    return space === "student" || space === "parent"
}

/**
 * L'élève qu'on regarde. Sans `studentId` : l'élève du compte (aucun pour un tuteur). Avec : cet
 * élève parmi ceux du compte — le compte lui-même s'il porte cet id, sinon la liste du tuteur. Un
 * élève que le tuteur n'a pas encore chargé rend `null` : l'écran le charge (`getStudent`).
 */
export function selectDisplayedStudent(state: ReduxState, studentId?: StudentId): StoredUser | null {
    const account = selectAccount(state)
    if (account == null) return null
    if (studentId == null || String(account.id) === String(studentId)) {
        return accountIsTheStudent(state) ? account : null
    }
    return (account.students as StoredUser[] | undefined)?.find((s) => String(s?.id) === String(studentId)) ?? null
}

/** `[compte]`, la même référence tant que le compte ne change pas. */
const selectSelfList = createSelector([selectAccount], (account): StoredUser[] => [account])

/**
 * Ses élèves : ceux dont le store porte les données. La liste du tuteur ; l'élève lui-même pour un
 * élève ou un parent — passer à un autre enfant est le sélecteur de profil, l'enfant est un autre compte.
 */
export function selectAccountStudents(state: ReduxState): StoredUser[] {
    const account = selectAccount(state)
    if (account == null) return NO_STUDENTS
    if (!accountIsTheStudent(state)) return (account.students as StoredUser[] | undefined) ?? NO_STUDENTS
    return selectSelfList(state)
}

/** La génération d'avatar en cours du compte — un état d'écran que le réducteur `currentUser` porte à côté. */
export function selectAvatarGeneration(state: ReduxState): { isGenerating?: boolean } | null {
    return state?.data?.currentUser?.avatarGenerating ?? null
}
